import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faTrash, faChevronDown, faChevronRight, faCheck, faSquare, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../../components/ConfirmModal';
import { perfilService } from '../../../services/perfilService';
import { permissaoService } from '../../../services/permissaoService';

const PerfilEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [permissoesPorModulo, setPermissoesPorModulo] = useState({});
  const [loadingPermissoes, setLoadingPermissoes] = useState(true);
  const [modulosExpandidos, setModulosExpandidos] = useState({});
  const [filtroPermissoes, setFiltroPermissoes] = useState('');
  
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    nivel_acesso: 1,
    ativo: true,
    permissoes: []
  });

  useEffect(() => {
    loadPerfil();
    loadPermissoes();
  }, [id]);

  const loadPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const response = await permissaoService.getByModule();
      setPermissoesPorModulo(response.data || {});
      
      // Expandir todos os módulos por padrão
      const modulos = Object.keys(response.data || {});
      const expandidos = {};
      modulos.forEach(modulo => {
        expandidos[modulo] = true;
      });
      setModulosExpandidos(expandidos);
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const loadPerfil = async () => {
    try {
      setLoading(true);
      const response = await perfilService.getById(id);
      
      // Carregar permissões do perfil
      let perfilPermissoes = [];
      try {
        const permissoesResponse = await perfilService.getPermissions(id);
        perfilPermissoes = permissoesResponse.data?.map(p => p.id) || [];
      } catch (err) {
        console.error('Erro ao carregar permissões do perfil:', err);
      }

      setFormData({
        nome: response.data.nome || '',
        descricao: response.data.descricao || '',
        nivel_acesso: response.data.nivel_acesso || 1,
        ativo: response.data.ativo || false,
        permissoes: perfilPermissoes
      });
    } catch (err) {
      setError('Erro ao carregar perfil: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissaoChange = (permissaoId) => {
    setFormData(prev => ({
      ...prev,
      permissoes: prev.permissoes.includes(permissaoId)
        ? prev.permissoes.filter(id => id !== permissaoId)
        : [...prev.permissoes, permissaoId]
    }));
  };

  const handleSelectAllPermissoes = () => {
    const permissoesFiltradas = getPermissoesFiltradas();
    const todasPermissoesFiltradas = [];
    Object.values(permissoesFiltradas).forEach(modulo => {
      modulo.forEach(permissao => {
        todasPermissoesFiltradas.push(permissao.id);
      });
    });
    
    // Verifica se todas as permissões filtradas estão selecionadas
    const todasSelecionadas = todasPermissoesFiltradas.every(id => formData.permissoes.includes(id));
    
    if (todasSelecionadas) {
      // Remove todas as permissões filtradas da seleção
      setFormData(prev => ({
        ...prev,
        permissoes: prev.permissoes.filter(id => !todasPermissoesFiltradas.includes(id))
      }));
    } else {
      // Adiciona todas as permissões filtradas à seleção
      setFormData(prev => ({
        ...prev,
        permissoes: [...new Set([...prev.permissoes, ...todasPermissoesFiltradas])]
      }));
    }
  };

  const handleSelectModulePermissoes = (modulo) => {
    const permissoesModulo = permissoesPorModulo[modulo]?.map(p => p.id) || [];
    const todasSelecionadas = permissoesModulo.every(id => formData.permissoes.includes(id));
    
    setFormData(prev => ({
      ...prev,
      permissoes: todasSelecionadas 
        ? prev.permissoes.filter(id => !permissoesModulo.includes(id))
        : [...new Set([...prev.permissoes, ...permissoesModulo])]
    }));
  };

  const toggleModulo = (modulo) => {
    setModulosExpandidos(prev => ({
      ...prev,
      [modulo]: !prev[modulo]
    }));
  };

  const getTotalPermissoes = () => {
    let total = 0;
    Object.values(permissoesPorModulo).forEach(modulo => {
      total += modulo.length;
    });
    return total;
  };

  const getTotalPermissoesFiltradas = () => {
    let total = 0;
    Object.values(getPermissoesFiltradas()).forEach(modulo => {
      total += modulo.length;
    });
    return total;
  };

  const filtrarPermissoes = (permissoes) => {
    if (!filtroPermissoes) return permissoes;
    
    return permissoes.filter(permissao => 
      permissao.descricao?.toLowerCase().includes(filtroPermissoes.toLowerCase()) ||
      permissao.acao?.toLowerCase().includes(filtroPermissoes.toLowerCase()) ||
      permissao.nome?.toLowerCase().includes(filtroPermissoes.toLowerCase())
    );
  };

  const getPermissoesFiltradas = () => {
    const resultado = {};
    Object.entries(permissoesPorModulo).forEach(([modulo, permissoes]) => {
      const permissoesFiltradas = filtrarPermissoes(permissoes);
      if (permissoesFiltradas.length > 0) {
        resultado[modulo] = permissoesFiltradas;
      }
    });
    return resultado;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    setSaving(true);
    setError('');
    setShowSaveModal(false);

    try {
      // Atualizar dados básicos do perfil
      await perfilService.update(id, {
        nome: formData.nome,
        descricao: formData.descricao,
        nivel_acesso: formData.nivel_acesso,
        ativo: formData.ativo
      });

      // Atualizar permissões do perfil
      await perfilService.updatePermissions(id, formData.permissoes);

      navigate('/seguranca/perfis');
    } catch (err) {
      setError('Erro ao atualizar perfil: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setShowDeleteModal(false);
      const response = await perfilService.delete(id);
      console.log('Perfil deletado:', response);
      navigate('/seguranca/perfis');
    } catch (err) {
      console.error('Erro ao excluir perfil:', err);
      setError('Erro ao excluir perfil: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate('/seguranca/perfis');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="text-gray-600 hover:text-gray-800"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Editar Perfil</h1>
            <p className="text-gray-600">Editar informações do perfil</p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit}>
          {/* Mensagem de erro */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Perfil *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Digite o nome do perfil"
              />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Digite a descrição do perfil"
              />
            </div>

            {/* Nível de Acesso */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nível de Acesso *
              </label>
              <select
                name="nivel_acesso"
                value={formData.nivel_acesso}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value={1}>Nível 1 - Básico</option>
                <option value={2}>Nível 2 - Intermediário</option>
                <option value={3}>Nível 3 - Avançado</option>
                <option value={4}>Nível 4 - Administrador</option>
                <option value={5}>Nível 5 - Super Admin</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="ativo"
                  checked={formData.ativo}
                  onChange={handleChange}
                  className="mr-2 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">Perfil ativo</span>
              </label>
            </div>

            {/* Permissões */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissões
              </label>
              {loadingPermissoes ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                  <span className="ml-2 text-gray-600">Carregando permissões...</span>
                </div>
              ) : (
                <div className="border border-gray-300 rounded-md">
                  {/* Cabeçalho com seleção geral e filtro */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 space-y-3">
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const permissoesFiltradas = getPermissoesFiltradas();
                            const todasPermissoesFiltradas = [];
                            Object.values(permissoesFiltradas).forEach(modulo => {
                              modulo.forEach(permissao => {
                                todasPermissoesFiltradas.push(permissao.id);
                              });
                            });
                            return todasPermissoesFiltradas.length > 0 && 
                                   todasPermissoesFiltradas.every(id => formData.permissoes.includes(id));
                          })()}
                          onChange={handleSelectAllPermissoes}
                          className="mr-3 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {filtroPermissoes ? 'Selecionar todas visíveis' : 'Selecionar todas as permissões'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formData.permissoes.length} de {getTotalPermissoes()} selecionadas
                        {filtroPermissoes && (
                          <span className="block">
                            {getTotalPermissoesFiltradas()} visíveis de {getTotalPermissoes()} total
                          </span>
                        )}
                      </span>
                    </label>
                    
                    {/* Campo de filtro */}
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={filtroPermissoes}
                        onChange={(e) => setFiltroPermissoes(e.target.value)}
                        className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Filtrar permissões por descrição ou ação..."
                      />
                      {filtroPermissoes && (
                        <button
                          type="button"
                          onClick={() => setFiltroPermissoes('')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Lista de módulos */}
                  <div className="max-h-96 overflow-y-auto">
                    {Object.keys(getPermissoesFiltradas()).length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {filtroPermissoes ? 'Nenhuma permissão encontrada com o filtro aplicado' : 'Nenhuma permissão disponível'}
                      </div>
                    ) : (
                      Object.entries(getPermissoesFiltradas()).map(([modulo, permissoes]) => {
                        const permissoesModulo = permissoes.map(p => p.id);
                        const todasSelecionadas = permissoesModulo.every(id => formData.permissoes.includes(id));
                        const algumasSelecionadas = permissoesModulo.some(id => formData.permissoes.includes(id));
                        
                        return (
                          <div key={modulo} className="border-b border-gray-100 last:border-b-0">
                            {/* Cabeçalho do módulo */}
                            <div className="px-4 py-3 bg-gray-25 hover:bg-gray-50 transition-colors">
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => toggleModulo(modulo)}
                                  className="flex items-center flex-1 text-left focus:outline-none"
                                >
                                  <FontAwesomeIcon 
                                    icon={modulosExpandidos[modulo] ? faChevronDown : faChevronRight}
                                    className="mr-2 text-gray-400 text-xs"
                                  />
                                  <span className="text-sm font-medium text-gray-800 capitalize">
                                    {modulo}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({permissoes.length} permissões)
                                  </span>
                                </button>
                                
                                <label className="flex items-center ml-3" onClick={(e) => e.stopPropagation()}>
                                  <div className="relative">
                                    <input
                                      type="checkbox"
                                      checked={todasSelecionadas}
                                      onChange={() => handleSelectModulePermissoes(modulo)}
                                      className="mr-2 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    {algumasSelecionadas && !todasSelecionadas && (
                                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="w-2 h-2 bg-green-600 rounded-sm"></div>
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-600">
                                    {todasSelecionadas ? 'Todas' : algumasSelecionadas ? 'Algumas' : 'Nenhuma'}
                                  </span>
                                </label>
                              </div>
                            </div>

                            {/* Lista de permissões do módulo */}
                            {modulosExpandidos[modulo] && (
                              <div className="px-6 py-2 space-y-2">
                                {permissoes.map((permissao) => (
                                  <label key={permissao.id} className="flex items-start py-2 hover:bg-gray-50 rounded px-2 -mx-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={formData.permissoes.includes(permissao.id)}
                                      onChange={() => handlePermissaoChange(permissao.id)}
                                      className="mr-3 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900">
                                        {permissao.descricao || permissao.nome}
                                      </div>
                                      <div className="text-xs text-blue-600 mt-1 font-medium">
                                        Ação: {permissao.acao}
                                      </div>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Use o filtro para encontrar permissões específicas e organize por módulo
              </p>
            </div>
          </div>

          {/* Botões */}
          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
             aria-label="Cancelar">
              <FontAwesomeIcon icon={faTimes} />
              <span className="hidden sm:inline">Cancelar</span>
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTrash} />
              <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
            </button>            
            <button
              type="submit"
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
             aria-label="Salvar">
              <FontAwesomeIcon icon={faSave} />
              <span className="hidden sm:inline">{saving ? 'Salvando...' : 'Salvar'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o perfil "${formData.nome}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal de Confirmação de Salvamento */}
      <ConfirmModal
        show={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={confirmSave}
        title="Confirmar Alterações"
        message={`Tem certeza que deseja salvar as alterações no perfil "${formData.nome}"?`}
        confirmText="Salvar"
        cancelText="Cancelar"
        type="success"
      />
    </div>
  );
};

export default PerfilEdit;
